/** Shared structural tree for rendered Markdown and authoritative quote text.
 * Спільне структурне дерево відображення Markdown і перевірки цитат. */
export interface MarkdownSummaryNode {
  type?: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: MarkdownSummaryNode[];
}

const titles = new Map([
  ["підсумок", "Підсумок"],
  ["висновок", "Підсумок"],
  ["summary", "Summary"],
  ["conclusion", "Summary"],
]);

function text(node: MarkdownSummaryNode): string {
  return node.value ?? (node.children ?? []).map(text).join("");
}

function title(node: MarkdownSummaryNode): string | undefined {
  return titles.get(text(node).trim().replace(/[.:]$/, "").toLowerCase());
}

/** Accept explicit paragraph labels even when a model omits bold Markdown.
 * Явні позначки абзацу працюють і без жирного оформлення Markdown. */
function paragraphSummary(node: MarkdownSummaryNode) {
  if (node.tagName !== "p") return undefined;
  const [first, ...rest] = node.children ?? [];
  if (!first) return undefined;
  if (first.tagName === "strong") {
    const label = title(first);
    if (!label) return undefined;
    const [next, ...tail] = rest;
    return {
      label,
      body:
        next?.type === "text"
          ? [
              {
                ...next,
                value: text(next).replace(
                  /[.:]$/u.test(text(first).trim()) ? /^\s*/u : /^\s*[:.]?\s*/u,
                  "",
                ),
              },
              ...tail,
            ]
          : rest,
    };
  }
  if (first.type !== "text") return undefined;
  const match = /^\s*(підсумок|висновок|summary|conclusion)\s*:\s*/iu.exec(text(first));
  if (!match) return undefined;
  return {
    label: titles.get(match[1]!.toLowerCase())!,
    body: [{ ...first, value: text(first).slice(match[0].length) }, ...rest],
  };
}

function headingDepth(node: MarkdownSummaryNode): number {
  return /^h[1-6]$/.test(node.tagName ?? "") ? Number(node.tagName![1]) : 0;
}

function panel(
  label: string,
  heading: MarkdownSummaryNode,
  body: MarkdownSummaryNode[],
): MarkdownSummaryNode {
  return {
    type: "element",
    tagName: "section",
    properties: { className: ["rk-chat-summary"] },
    children: [
      {
        ...heading,
        properties: { ...heading.properties, className: ["rk-chat-summary-title"] },
        children: [{ type: "text", value: label }],
      },
      ...body,
    ],
  };
}

/** Style explicit top-level Markdown labels; never infer a paragraph's meaning.
 * Оформлювати лише явні верхні позначки Markdown, не вгадувати зміст абзацу. */
export function summaryPanels() {
  return (tree: MarkdownSummaryNode) => {
    const nodes = tree.children ?? [];
    const output: MarkdownSummaryNode[] = [];
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]!;
      const depth = headingDepth(node);
      const paragraph = paragraphSummary(node);
      const standalone = paragraph?.body.every(
        (child) => child.type === "text" && !text(child).trim(),
      );
      const label = depth ? title(node) : standalone ? paragraph?.label : undefined;
      if (label) {
        let end = i + 1;
        while (end < nodes.length) {
          const next = nodes[end]!;
          const nextDepth = headingDepth(next);
          if (next.tagName === "hr" || (nextDepth && nextDepth <= (depth || 6))) break;
          end++;
        }
        if (nodes.slice(i + 1, end).some((child) => child.type === "element")) {
          output.push(
            panel(
              label,
              depth ? node : { type: "element", tagName: "h3" },
              nodes.slice(i + 1, end),
            ),
          );
          i = end - 1;
          continue;
        }
      }
      if (paragraph && !standalone) {
        output.push(
          panel(paragraph.label, { type: "element", tagName: "h3" }, [
            { ...node, children: paragraph.body },
          ]),
        );
      } else {
        output.push(node);
      }
    }
    tree.children = output;
  };
}
